import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { SystemNotification } from "ng/src/app/notifications/notification.model";
import { SystemNotificationService } from "ng/src/app/notifications/system-notification.service";
import { BehaviorSubject, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PrimeIcons } from "primeng/api";

@Component({
	selector: 'system-notification-entry[notification]',
	template: `
		<div *ngIf="getNotification() as notification" class="p-d-flex p-flex-column">
			<div *ngIf="showNavigation" class="p-grid">
				<div class="p-md-4 p-col-6">
                    <p-button (onClick)="setFromIndex(indexOfNotification - 1)" [disabled]="!hasPrev" styleClass="w-100 p-button-secondary">
                        <div class="p-d-flex p-jc-center p-ai-center w-100">
                            <i [class]="Icons.ARROW_LEFT"></i>
                            <span class="p-ml-2">Previous</span>
                        </div>
                    </p-button>
				</div>
				<div class="p-md-4 p-md-offset-4 p-col-6">
					<p-button (onClick)="setFromIndex(indexOfNotification + 1)" [disabled]="!hasNext" styleClass="w-100 p-button-secondary">
						<div class="p-d-flex p-jc-center p-ai-center w-100">
							<span class="p-mr-2">Next</span>
							<i [class]="Icons.ARROW_RIGHT"></i>
						</div>
					</p-button>
				</div>
			</div>
			<div class="p-d-flex p-ai-center p-flex-column">
				<h3>{{notification.title}}</h3>
				<h5 class="p-mt-0">{{notification.created | date: 'MM/dd/yyyy HH:mm'}}</h5>
			</div>
			<div class="well markdown-view note-table-note" [innerHtml]="notification.content | trustHtml"></div>
		</div>
	`,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SystemNotificationEntryComponent implements OnInit, OnDestroy {
	@Input()
	set notification(val: SystemNotification) {
		this.updateNotificationDisplay(val);
	}
	
	@Input()
	showNavigation = true;
	
	private notificationSubject = new BehaviorSubject<SystemNotification | undefined>(undefined);
	notifications: SystemNotification[] = [];
	
	private readonly destroySubject = new Subject();
	readonly onDestroy$ = this.destroySubject.asObservable();
	
	readonly Icons = PrimeIcons;
	
	get hasNext() {
		return !!this.notifications[this.indexOfNotification + 1];
	}
	
	get hasPrev() {
		return !!this.notifications[this.indexOfNotification - 1];
	}
	
	indexOfNotification = -1;
	
	constructor(
		private systemNotificationService: SystemNotificationService,
		readonly changeRef: ChangeDetectorRef
	) {}
	
	ngOnInit(): void {
		this.systemNotificationService.getPoll()
			.pipe(
				takeUntil(this.onDestroy$)
			)
			.subscribe(n => {
				if (Array.isArray(n)) {
					this.notifications = n;
					this.updateNotificationDisplay(this.notificationSubject.value);
				}
			});
	}
	
	ngOnDestroy(): void {
		this.destroySubject.next();
	}
	
	getNotification() {
		return this.notificationSubject.value;
	}
	
	setFromIndex(index: number) {
		this.updateNotificationDisplay(this.notifications[index]);
	}
	
	updateNotificationDisplay(notification: SystemNotification | undefined) {
		if (notification) {
			this.indexOfNotification = this.notifications.findIndex(n => n.id === notification.id);
			
			if (notification.id !== this.getNotification()?.id) {
				this.systemNotificationService.updateLastSeen(notification).subscribe(() => this.changeRef.markForCheck());
			}
		}
		else {
			this.indexOfNotification = -1;
		}
		
		this.notificationSubject.next(notification);
		this.changeRef.markForCheck();
	}
}